import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { submitContactForm } from '../services/api';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  pt: {
    eyebrow: 'Conversa privada · Your Broker Portugal',
    heroTitle: 'Iniciar uma',
    heroTitleEm: 'conversa privada.',
    heroLead:
      'Trinta minutos, por vídeo ou pessoalmente, sem agenda comercial. Compreendemos o seu projeto, partilhamos o nosso modelo, e indicamos honestamente se somos o parceiro certo para o representar.',

    leftEyebrow: 'O que esperar',
    leftItems: [
      { title: 'Resposta em 24 horas', body: 'Lemos cada mensagem pessoalmente. Não delegamos o primeiro contacto.' },
      { title: 'Briefing curto', body: 'Trinta minutos. Sem PowerPoints. Sem agenda comercial. Só uma conversa.' },
      { title: 'Discrição absoluta', body: 'O seu nome, o seu orçamento, os seus motivos. Nada chega a terceiros.' },
      { title: 'Decisão informada', body: 'Saímos do briefing com clareza mútua sobre se faz sentido avançar.' },
    ],

    nameLabel: 'Nome',
    emailLabel: 'Email',
    whatsappLabel: 'WhatsApp ou telefone',
    intentionLabel: 'O seu projeto, em duas linhas',
    intentionPlaceholder: 'Ex: Procuro uma villa de 4 quartos em Cascais para residência principal, com orçamento até €3,5M, decisão em 6 meses.',
    submit: 'Enviar pedido privado',
    submitting: 'A enviar…',
    successTitle: 'Obrigado.',
    successBody: 'Responderemos pessoalmente dentro de 24 horas, do email institucional portugal@yourbrokerportugal.com.',

    contactEyebrow: 'Outros canais',
    contactBody: 'Para conversas mais imediatas, encontre-nos abaixo. Atendemos das 09h00 às 19h00 (Lisboa).',

    errorMsg: 'Não conseguimos enviar a sua mensagem. Tente de novo ou envie-nos um email direto.',
  },
  en: {
    eyebrow: 'Private conversation · Your Broker Portugal',
    heroTitle: 'Begin a',
    heroTitleEm: 'private conversation.',
    heroLead:
      'Thirty minutes, by video or in person, with no commercial agenda. We understand your project, share our model, and tell you honestly whether we are the right partner to represent you.',

    leftEyebrow: 'What to expect',
    leftItems: [
      { title: 'Reply within 24 hours', body: 'We read every message personally. We do not delegate first contact.' },
      { title: 'Short briefing', body: 'Thirty minutes. No PowerPoints. No commercial agenda. Just a conversation.' },
      { title: 'Absolute discretion', body: 'Your name, your budget, your motives. Nothing reaches third parties.' },
      { title: 'Informed decision', body: 'We leave the briefing with mutual clarity on whether it makes sense to proceed.' },
    ],

    nameLabel: 'Name',
    emailLabel: 'Email',
    whatsappLabel: 'WhatsApp or phone',
    intentionLabel: 'Your project, in two lines',
    intentionPlaceholder: 'Ex: Looking for a 4-bedroom villa in Cascais as primary residence, budget up to €3.5M, decision within 6 months.',
    submit: 'Send private enquiry',
    submitting: 'Sending…',
    successTitle: 'Thank you.',
    successBody: 'We will respond personally within 24 hours, from portugal@yourbrokerportugal.com.',

    contactEyebrow: 'Other channels',
    contactBody: 'For more immediate conversations, find us below. We are available from 09:00 to 19:00 (Lisbon time).',

    errorMsg: 'We could not send your message. Please try again or email us directly.',
  },
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-visible'), io.unobserve(e.target))),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const editorialInput = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.98rem',
  fontWeight: 300,
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid rgba(28,28,26,0.18)',
  borderRadius: 0,
  padding: '0.85rem 0',
  width: '100%',
  color: '#1c1c1a',
  outline: 'none',
  transition: 'border-color 0.3s ease',
};

const editorialLabel = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.62rem',
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  fontWeight: 500,
  color: '#b8955a',
  display: 'block',
  marginBottom: '0.5rem',
};

const ContactoPage = () => {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const { title: seoTitle, description: seoDescription } = usePageSEO('contacto', language);
  const { toast } = useToast();
  const ref = useReveal();

  const [homeContent, setHomeContent] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', observations: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/home-content`).then((r) => setHomeContent(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Backend contact schema includes privacyAccepted (boolean) — we treat the
      // act of submitting this editorial form as implicit acceptance per the
      // privacy notice immediately above the submit button.
      await submitContactForm({ ...form, location: '', priceRange: '', privacyAccepted: true });
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', observations: '' });
    } catch (err) {
      toast({
        title: language === 'pt' ? 'Erro' : 'Error',
        description: copy.errorMsg,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} url="https://yourbrokerportugal.com/contact" />
      <style>{`
        body { background: #f7f4ef; }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity .95s ease, transform .95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
        .editorial-input:focus { border-bottom-color: #b8955a !important; }
        .editorial-textarea { min-height: 110px; resize: vertical; }
      `}</style>

      <Header companyName={homeContent?.headerFooter?.companyName} logo={homeContent?.headerFooter?.logo} />

      <main ref={ref}>
        {/* HERO */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-5xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.eyebrow}</Eyebrow>
            </div>
            <h1
              className="rv text-editorial-charcoal font-light mt-7 mb-9 max-w-4xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem, 4.4vw, 4rem)', lineHeight: '1.08' }}
              data-testid="contact-hero-title"
            >
              {copy.heroTitle}{' '}
              <em className="italic text-editorial-bronze">{copy.heroTitleEm}</em>
            </h1>
            <p
              className="rv text-editorial-muted font-light max-w-2xl"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
            >
              {copy.heroLead}
            </p>
          </div>
        </section>

        {/* SPLIT: left = expectations / right = form */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-32 bg-editorial-sand/40">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
            {/* Left: editorial copy */}
            <div className="lg:col-span-5">
              <div className="rv">
                <Eyebrow>{copy.leftEyebrow}</Eyebrow>
              </div>
              <div className="rv mt-10 space-y-9">
                {copy.leftItems.map((item, i) => (
                  <div key={item.title}>
                    <p
                      className="text-editorial-bronze mb-2"
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
                    >
                      0{i + 1}
                    </p>
                    <h3
                      className="text-editorial-charcoal font-light mb-2"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.45rem', lineHeight: '1.2' }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-editorial-muted font-light max-w-md"
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', lineHeight: '1.88' }}
                    >
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: minimalist form */}
            <div className="lg:col-span-7 lg:pl-10 lg:border-l" style={{ borderColor: 'rgba(28,28,26,0.08)' }}>
              {success ? (
                <div className="rv pt-2" data-testid="contact-success">
                  <Eyebrow>{copy.eyebrow}</Eyebrow>
                  <h2
                    className="text-editorial-charcoal font-light mt-7 mb-7"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: '1.15' }}
                  >
                    {copy.successTitle}
                  </h2>
                  <p
                    className="text-editorial-muted font-light max-w-lg"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
                  >
                    {copy.successBody}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="rv space-y-10" data-testid="contact-form">
                  <div>
                    <label style={editorialLabel} htmlFor="contact-name">
                      {copy.nameLabel}
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="editorial-input"
                      style={editorialInput}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label style={editorialLabel} htmlFor="contact-email">
                      {copy.emailLabel}
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="editorial-input"
                      style={editorialInput}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label style={editorialLabel} htmlFor="contact-phone">
                      {copy.whatsappLabel}
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      className="editorial-input"
                      style={editorialInput}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label style={editorialLabel} htmlFor="contact-observations">
                      {copy.intentionLabel}
                    </label>
                    <textarea
                      id="contact-observations"
                      name="observations"
                      required
                      value={form.observations}
                      onChange={handleChange}
                      placeholder={copy.intentionPlaceholder}
                      className="editorial-input editorial-textarea"
                      style={{ ...editorialInput, minHeight: '110px', resize: 'vertical' }}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center text-editorial-bone bg-editorial-charcoal hover:bg-editorial-charcoal-soft transition-all duration-300 hover:-translate-y-0.5 py-4 px-10 disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '0.65rem',
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        borderRadius: 0,
                      }}
                      data-testid="contact-submit-button"
                    >
                      {submitting ? copy.submitting : copy.submit}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* OTHER CHANNELS */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-24 bg-editorial-mineral">
          <div className="max-w-5xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.contactEyebrow}</Eyebrow>
              <p
                className="text-editorial-muted font-light mt-7 max-w-2xl mb-10"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.contactBody}
              </p>
            </div>
            <div className="rv grid grid-cols-1 sm:grid-cols-3 gap-10">
              <div>
                <p style={editorialLabel}>Email</p>
                <a
                  href="mailto:portugal@yourbrokerportugal.com"
                  className="text-editorial-charcoal hover:text-editorial-bronze transition-colors border-b border-editorial-sand-deep hover:border-editorial-bronze pb-0.5"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', fontWeight: 300 }}
                  data-testid="contact-channel-email"
                >
                  portugal@yourbrokerportugal.com
                </a>
              </div>
              <div>
                <p style={editorialLabel}>{language === 'en' ? 'Phone' : 'Telefone'}</p>
                <a
                  href="tel:+351931997415"
                  className="text-editorial-charcoal hover:text-editorial-bronze transition-colors border-b border-editorial-sand-deep hover:border-editorial-bronze pb-0.5"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', fontWeight: 300 }}
                  data-testid="contact-channel-phone"
                >
                  +351 931 997 415
                </a>
              </div>
              <div>
                <p style={editorialLabel}>WhatsApp</p>
                <a
                  href="https://wa.me/351931997415"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-editorial-charcoal hover:text-editorial-bronze transition-colors border-b border-editorial-sand-deep hover:border-editorial-bronze pb-0.5"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', fontWeight: 300 }}
                  data-testid="contact-channel-whatsapp"
                >
                  {language === 'en' ? 'Open WhatsApp' : 'Abrir WhatsApp'}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default ContactoPage;
